import { Menu, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import JobItem from '@jobs/models/dto/job-item';
import { formatDate } from '@lib/dateUtils';
import { useDisableJob, useEnableJob } from '@jobs/mutations/jobMutations';
import useJDialog from '@/hooks/useJDialog';
import { JButton } from '@atoms/JButton';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import useJSnackbar from '@/hooks/useJSnackbar';
import JobStatusEnum from '@jobs/models/job-status-enum';
import { useNavigate } from 'react-router-dom';
import Paths from '@/routes/paths';
import { getJobStatus } from '@jobs/utils';
import { StatusChip } from '@atoms/StatusChip';
import { TableRowStyled } from '@styled/table-row.styled';
import JTableCell from '@atoms/JTabelCell';
import { ShowMoreButton } from '@atoms/ShowMoreButton';
import { MenuItemStyled } from '@styled/menu-item.styled';
import { DialogContentStyled } from '@styled/dialog-content-styled';

type enableMutationType = UseMutateFunction<
  AxiosResponse<null, any>,
  unknown,
  { id: string },
  unknown
>;

type disbaleMutationType = UseMutateFunction<
  AxiosResponse<null, any>,
  unknown,
  { id: string },
  unknown
>;

interface Props {
  row: JobItem;
}

const JobRow = ({ row }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const {
    id,
    title,
    referenceNumber,
    createdAt,
    establishment,
    publicationStartDate,
    publicationEndDate,
    deletedAt,
    jobEndDate,
    jobStartDate,
  } = row;

  const [rowMenuanchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const {
    mutate: enableJobMutate,
    isSuccess: enableSuccess,
    isError: enableError,
  } = useEnableJob();
  const {
    mutate: disableJobMutate,
    isSuccess: disableSuccess,
    isError: disableError,
  } = useDisableJob();
  const { showJDialog, closeJDialog } = useJDialog();
  const { showSnackbar } = useJSnackbar();
  const queryClient = useQueryClient();

  const [jobStatus, setJobStatus] = useState<JobStatusEnum>();
  const open = Boolean(rowMenuanchorEl);

  const openDisableDialog = (
    jobTitle: string,
    jobId: string,
    disableMutation: disbaleMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      t('DISABLE_JOB_TITLE'),
      <DisableDialogContent
        jobId={jobId}
        establishmentName={establishment?.name}
        jobTitle={jobTitle}
        disableMutation={disableMutation}
      />,
    );
  };
  const openEnableDialog = (
    jobTitle: string,
    jobId: string,
    enableMutation: enableMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      t('ENABLE_JOB_TITLE'),
      <EnableDialogContent
        jobId={jobId}
        jobTitle={jobTitle}
        enableMutation={enableMutation}
      />,
    );
  };
  const handleChangeStatus = (isStatusActive: boolean) => {
    isStatusActive
      ? openDisableDialog(`${title}`, id, disableJobMutate)
      : openEnableDialog(title, id, enableJobMutate);
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRowMenuAnchorEl(event.currentTarget);
  };
  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
  };

  useEffect(() => {
    setJobStatus(
      getJobStatus(!!deletedAt, publicationStartDate, publicationEndDate),
    );
  }, []);

  useEffect(() => {
    if (!enableSuccess) return;
    queryClient.invalidateQueries(['jobs']);
    setJobStatus(getJobStatus(false, publicationStartDate, publicationEndDate));
    closeJDialog();
  }, [enableSuccess]);
  useEffect(() => {
    if (!disableSuccess) return;
    queryClient.invalidateQueries(['jobs']);
    setJobStatus(getJobStatus(true, publicationStartDate, publicationEndDate));
    closeJDialog();
  }, [disableSuccess]);

  useEffect(() => {
    if (enableError) showSnackbar(t('API_ERROR'), 'error');
  }, [enableError]);
  useEffect(() => {
    if (disableError) showSnackbar(t('API_ERROR'), 'error');
  }, [disableError]);

  return (
    <TableRowStyled key={id} data-test={`job-list-row-${referenceNumber}`}>
      <JTableCell isbold>{title}</JTableCell>
      <JTableCell>{establishment?.enterprise?.name}</JTableCell>
      <JTableCell>{establishment?.name}</JTableCell>
      <JTableCell>{formatDate(createdAt)}</JTableCell>
      <JTableCell>{referenceNumber}</JTableCell>
      <JTableCell>{formatDate(jobStartDate)}</JTableCell>
      <JTableCell>{formatDate(jobEndDate)}</JTableCell>
      <JTableCell>
        <StatusChip
          label={t(
            getJobStatus(
              !!row.deletedAt,
              publicationStartDate,
              publicationEndDate,
            ).toUpperCase(),
          )}
          variant={
            jobStatus === JobStatusEnum.PUBLISHED ||
            jobStatus === JobStatusEnum.PENDING_PUBLICATION
              ? 'success'
              : 'danger'
          }
        ></StatusChip>
      </JTableCell>

      <JTableCell>
        <ShowMoreButton onClick={handleClick} open={open} />
        <Menu
          id="basic-menu"
          anchorEl={rowMenuanchorEl}
          open={open}
          onClose={handleRowMenuClose}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItemStyled
            data-qa="change-status"
            onClick={() =>
              handleChangeStatus(
                jobStatus === JobStatusEnum.PUBLISHED ||
                  jobStatus === JobStatusEnum.PENDING_PUBLICATION,
              )
            }
            red={
              jobStatus === JobStatusEnum.PUBLISHED ||
              jobStatus === JobStatusEnum.PENDING_PUBLICATION
            }
            green={jobStatus !== JobStatusEnum.PUBLISHED}
          >
            {jobStatus === JobStatusEnum.PUBLISHED ||
            jobStatus === JobStatusEnum.PENDING_PUBLICATION
              ? t('DESACTIVATE')
              : t('ACTIVATE')}
          </MenuItemStyled>

          <MenuItemStyled
            data-qa="navigate"
            onClick={() => navigate(Paths.JobsDetails.replace(':id', id))}
            red={false}
            green={false}
          >
            {t('JOB_DETAIL_BUTTON')}
          </MenuItemStyled>
        </Menu>
      </JTableCell>
    </TableRowStyled>
  );
};

const DisableDialogContent = ({
  jobTitle,
  jobId,
  establishmentName,
  disableMutation,
}: {
  jobTitle: string;
  jobId: string;
  establishmentName: string;
  disableMutation: disbaleMutationType;
}) => {
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <DialogContentStyled>
        {t('DISABLE_JOB_TEXT')}
        <strong> {jobTitle}</strong> ?{' '}
        {establishmentName && (
          <>
            {t('BELONG_TO_ESTABLISHMENT')} <strong> {establishmentName}</strong>
          </>
        )}
      </DialogContentStyled>

      <Stack direction={'row'} gap={1}>
        <JButton
          onClick={() => disableMutation({ id: jobId })}
          variant="contained"
          color="error"
        >
          {t('DISABLE_JOB_DISABLE_BUTTON')}
        </JButton>
        <JButton onClick={closeJDialog} variant="contained" color="info">
          Annuler
        </JButton>
      </Stack>
    </Stack>
  );
};

const EnableDialogContent = ({
  jobTitle,
  jobId,
  enableMutation,
}: {
  jobTitle: string;
  jobId: string;
  enableMutation: enableMutationType;
}) => {
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <DialogContentStyled>
        {t('ENABLE_JOB_TEXT')}
        <strong> {jobTitle}</strong> ?
      </DialogContentStyled>

      <Stack direction={'row'} gap={1}>
        <JButton
          onClick={() => enableMutation({ id: jobId })}
          variant="contained"
          color="secondary"
        >
          {t('ENABLE_JOB_DISABLE_BUTTON')}
        </JButton>
        <JButton onClick={closeJDialog} variant="contained" color="info">
          {t('CANCEL')}
        </JButton>
      </Stack>
    </Stack>
  );
};

export default JobRow;
